import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { clientApi, Client, ClientSearchParams } from '../../services/clientApi';
import {
  setLoading,
  setError,
  setItems,
  setSelectedItem,
  addItem,
  updateItem,
  removeItem,
  setValidationErrors,
  setPagination,
  setStatistics,
  setBulkOperationLoading,
  setSuccessMessage,
  clearSuccessMessage,
  FETCH_CLIENTS,
  FETCH_CLIENT_BY_ID,
  CREATE_CLIENT,
  UPDATE_CLIENT,
  DELETE_CLIENT,
  SEARCH_CLIENTS,
  FETCH_STATISTICS,
  BULK_ACTIVATE,
  BULK_DEACTIVATE,
  BULK_DELETE,
  EXPORT_TO_CSV,
} from '../reducers/clientSlice';

// Worker Sagas
function* fetchClientsSaga(action: PayloadAction<ClientSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const response = yield call(clientApi.getAll, params);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error fetching Clients:', error);
    yield put(setError(error.message || 'Failed to fetch Clients'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchClientByIdSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const client = yield call(clientApi.getById, action.payload);
    yield put(setSelectedItem(client));
  } catch (error: any) {
    console.error('Error fetching Client:', error);
    yield put(setError(error.message || 'Failed to fetch Client'));
  } finally {
    yield put(setLoading(false));
  }
}

function* createClientSaga(action: PayloadAction<Omit<Client, 'Id'>>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(clientApi.validation.validateClient, action.payload);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const newClient = yield call(clientApi.create, action.payload);
    yield put(addItem(newClient));
    yield put(setSuccessMessage('Client created successfully'));
    
    // Clear success message after 3 seconds
    setTimeout(() => {
      // Note: In a real saga, you'd use a different approach for this
      // This is simplified for demonstration
    }, 3000);
  } catch (error: any) {
    console.error('Error creating Client:', error);
    yield put(setError(error.message || 'Failed to create Client'));
  } finally {
    yield put(setLoading(false));
  }
}

function* updateClientSaga(action: PayloadAction<{ id: number; client: Partial<Client> }>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const { id, client } = action.payload;
    
    // Validate the request
    const validationErrors = yield call(clientApi.validation.validateClient, client);
    if (validationErrors.length > 0) {
      yield put(setValidationErrors(validationErrors));
      return;
    }
    
    const updatedClient = yield call(clientApi.update, id, client);
    yield put(updateItem(updatedClient));
    yield put(setSuccessMessage('Client updated successfully'));
  } catch (error: any) {
    console.error('Error updating Client:', error);
    yield put(setError(error.message || 'Failed to update Client'));
  } finally {
    yield put(setLoading(false));
  }
}

function* deleteClientSaga(action: PayloadAction<number>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(clientApi.delete, action.payload);
    if (response.success) {
      yield put(removeItem(action.payload));
      yield put(setSuccessMessage('Client deleted successfully'));
    } else {
      yield put(setError(response.message || 'Failed to delete Client'));
    }
  } catch (error: any) {
    console.error('Error deleting Client:', error);
    yield put(setError(error.message || 'Failed to delete Client'));
  } finally {
    yield put(setLoading(false));
  }
}

function* searchClientsSaga(action: PayloadAction<ClientSearchParams>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const response = yield call(clientApi.search, action.payload);
    
    yield put(setItems(response.data));
    yield put(setPagination({
      total: response.total,
      page: action.payload.page || 1,
      pageSize: action.payload.pageSize || 10,
    }));
  } catch (error: any) {
    console.error('Error searching Clients:', error);
    yield put(setError(error.message || 'Failed to search Clients'));
  } finally {
    yield put(setLoading(false));
  }
}

function* fetchStatisticsSaga(): Generator<any, void, any> {
  try {
    const statistics = yield call(clientApi.getStatistics);
    yield put(setStatistics(statistics));
  } catch (error: any) {
    console.error('Error fetching statistics:', error);
    // Don't set error for statistics as it's not critical
  }
}

function* bulkActivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(clientApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(clientApi.bulkActivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully activated ${action.payload.length} Clients`));
      // Refresh the list to show updated status
      yield call(fetchClientsSaga, { payload: undefined, type: FETCH_CLIENTS });
    } else {
      yield put(setError(response.message || 'Failed to activate Clients'));
    }
  } catch (error: any) {
    console.error('Error bulk activating Clients:', error);
    yield put(setError(error.message || 'Failed to activate Clients'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeactivateSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(clientApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(clientApi.bulkDeactivate, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deactivated ${action.payload.length} Clients`));
      // Refresh the list to show updated status
      yield call(fetchClientsSaga, { payload: undefined, type: FETCH_CLIENTS });
    } else {
      yield put(setError(response.message || 'Failed to deactivate Clients'));
    }
  } catch (error: any) {
    console.error('Error bulk deactivating Clients:', error);
    yield put(setError(error.message || 'Failed to deactivate Clients'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* bulkDeleteSaga(action: PayloadAction<number[]>): Generator<any, void, any> {
  try {
    yield put(setBulkOperationLoading(true));
    yield put(setError(null));
    
    // Validate the request
    const validationErrors = yield call(clientApi.validation.validateBulkOperation, action.payload);
    if (validationErrors.length > 0) {
      yield put(setError(validationErrors.join(', ')));
      return;
    }
    
    const response = yield call(clientApi.bulkDelete, action.payload);
    if (response.success) {
      yield put(setSuccessMessage(`Successfully deleted ${action.payload.length} Clients`));
      // Refresh the list to show updated items
      yield call(fetchClientsSaga, { payload: undefined, type: FETCH_CLIENTS });
    } else {
      yield put(setError(response.message || 'Failed to delete Clients'));
    }
  } catch (error: any) {
    console.error('Error bulk deleting Clients:', error);
    yield put(setError(error.message || 'Failed to delete Clients'));
  } finally {
    yield put(setBulkOperationLoading(false));
  }
}

function* exportToCSVSaga(action: PayloadAction<ClientSearchParams | undefined>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));
    yield put(setError(null));
    
    const params = action.payload || {};
    const blob = yield call(clientApi.exportToCSV, params);
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `clients-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    yield put(setSuccessMessage('Clients exported successfully'));
  } catch (error: any) {
    console.error('Error exporting Clients:', error);
    yield put(setError(error.message || 'Failed to export Clients'));
  } finally {
    yield put(setLoading(false));
  }
}

// Watcher Sagas
function* watchFetchClients(): Generator<any, void, any> {
  yield takeLatest(FETCH_CLIENTS, fetchClientsSaga);
}

function* watchFetchClientById(): Generator<any, void, any> {
  yield takeEvery(FETCH_CLIENT_BY_ID, fetchClientByIdSaga);
}

function* watchCreateClient(): Generator<any, void, any> {
  yield takeEvery(CREATE_CLIENT, createClientSaga);
}

function* watchUpdateClient(): Generator<any, void, any> {
  yield takeEvery(UPDATE_CLIENT, updateClientSaga);
}

function* watchDeleteClient(): Generator<any, void, any> {
  yield takeEvery(DELETE_CLIENT, deleteClientSaga);
}

function* watchSearchClients(): Generator<any, void, any> {
  yield takeLatest(SEARCH_CLIENTS, searchClientsSaga);
}

function* watchFetchStatistics(): Generator<any, void, any> {
  yield takeLatest(FETCH_STATISTICS, fetchStatisticsSaga);
}

function* watchBulkActivate(): Generator<any, void, any> {
  yield takeEvery(BULK_ACTIVATE, bulkActivateSaga);
}

function* watchBulkDeactivate(): Generator<any, void, any> {
  yield takeEvery(BULK_DEACTIVATE, bulkDeactivateSaga);
}

function* watchBulkDelete(): Generator<any, void, any> {
  yield takeEvery(BULK_DELETE, bulkDeleteSaga);
}

function* watchExportToCSV(): Generator<any, void, any> {
  yield takeEvery(EXPORT_TO_CSV, exportToCSVSaga);
}

// Root saga
export function* clientSaga(): Generator<any, void, any> {
  yield all([
    watchFetchClients(),
    watchFetchClientById(),
    watchCreateClient(),
    watchUpdateClient(),
    watchDeleteClient(),
    watchSearchClients(),
    watchFetchStatistics(),
    watchBulkActivate(),
    watchBulkDeactivate(),
    watchBulkDelete(),
    watchExportToCSV(),
  ]);
} 